import React, { useCallback, useState, useEffect } from "react";

import {
  PerformanceEditorWrapper,
  CreativesWrapper,
  RemoveButton,
  CreativeWrapper,
  EditButton
} from "./CampaignCreatePerformanceEditor.styled";

import { PerformanceIcon, AddIcon, ClearIcon } from "../Svg";
import {
  withRefluxStore,
  PreviewStore,
  PerformanceEditorStore,
  TemplatePostStore
} from "../../../../Stores";
import { removeShutterstockTransformations } from "../utils";
import Actions from "../../../../Actions";
import TemplatePostActions from "../TemplatePost/TemplatePostActions";
import PerformanceEditorActions from "./PerformanceEditorActions";
import { useTranslation } from "react-i18next";
import Creative from "./creative/Creative";
import { Tooltip } from "../../../../Components/Overlays";

import usePrevious from "../../../../hooks/usePrevious";
import { isEqual } from "lodash";
import { MEDIA_ASSET_CHANNELS } from "../media-assets-lab/constants";
import { duplicateMediaAsset } from "../hooks/useDuplicateMediaAsset";

import logger from "../../../../utils/LoggerFrontend";

async function updateCreativeAssets(creatives, newCreativeAssets = [], updatePost = () => {}) {
  const creativesWithoutTransformations = creatives.map(removeShutterstockTransformations);
  const creativeAssetsFiltered = newCreativeAssets.filter(
    (asset) =>
      creativesWithoutTransformations.includes(asset.url) ||
      creatives.includes(asset.shutterstock.unlicensedUrl)
  );

  const updates = {
    creativeAssets: creativeAssetsFiltered.map((asset) => ({
      id: asset.id,
      orientation: asset.orientation
    }))
  };

  updatePost(updates);
}

const PerformanceEditor = ({
  creatives = [],
  creativeAssets = [],
  mediaAssets = [],
  logos,
  editable,
  channel,
  allowMultipleCreativesPerPost,
  updatePost,
  padding,
  setSelectedCreativeIndexToEdit,
  selectedCreativeIndexToEdit,
  // PerformanceEditorStore
  performanceMediaAssets,
  isFetchingPerformanceMediaAsset,
  isDeletingMediaAsset,
  // TemplatePostStore
  inputProps
}) => {
  const { t } = useTranslation();
  const previousMediaAssets = usePrevious(mediaAssets);
  const previousInputProps = usePrevious(inputProps);
  const previousCreativeAssets = usePrevious(creativeAssets);

  const [refreshCreativeAssets, setRefreshCreativeAssets] = useState(false);
  const [renderedAssets, setRenderedAssets] = useState([]);
  const [showEditor, setShowEditor] = useState(renderedAssets?.length > 1);

  const renderCreativeAssets = () => {
    setRenderedAssets(creatives);
  };

  //Media assets logic
  const renderMediaAssets = ({ requestMediaAssets }) => {
    if (requestMediaAssets) {
      logger.log("CampaignCreatePerformanceEditor:renderMediaAssets", "Requesting media assets", {
        mediaAssets
      });
      return PerformanceEditorActions.getMultipleMediaAssetByIds(mediaAssets);
    } else {
      if (!performanceMediaAssets.length) return;
      setRenderedAssets(performanceMediaAssets);
    }
  };

  useEffect(() => {
    const hasMediaAssets = mediaAssets.length > 0;
    const hasPerformanceMediaAssets = performanceMediaAssets.length > 0;
    const isMediaAssetsChanged = previousMediaAssets !== mediaAssets;
    const isInputPropsChanged = previousInputProps !== inputProps;
    const canRenderMediaAssets = hasMediaAssets && MEDIA_ASSET_CHANNELS.includes(channel);
    const shouldRequestMediaAssets =
      !hasPerformanceMediaAssets || isMediaAssetsChanged || isInputPropsChanged;

    const requestMediaAssets =
      hasMediaAssets && !isFetchingPerformanceMediaAsset && shouldRequestMediaAssets;

    if (canRenderMediaAssets) {
      return renderMediaAssets({ requestMediaAssets });
    } else {
      return renderCreativeAssets();
    }
  }, [
    creatives,
    mediaAssets,
    performanceMediaAssets,
    isFetchingPerformanceMediaAsset,
    inputProps,
    creativeAssets,
    channel
  ]);

  useEffect(() => {
    if (renderedAssets.length > 1) {
      setShowEditor(true);
    }
  }, [renderedAssets]);
  //

  const openPerformanceEditor = useCallback(() => {
    if (window.analytics) {
      window.analytics.track("Apply Multiple Images Button Clicked", {
        source: "Campaign__Create_PerformanceEditor"
      });
    }

    setShowEditor(true);
  }, []);

  const addCreative = useCallback(async () => {
    const indexToDuplicate = creatives[selectedCreativeIndexToEdit]
      ? selectedCreativeIndexToEdit
      : 0;

    const newCreative = creatives[indexToDuplicate];
    const newCreatives = [...creatives, newCreative];
    const updates = {
      creatives: newCreatives
    };

    if (mediaAssets?.[indexToDuplicate]) {
      const newMediaAssetId = await duplicateMediaAsset(mediaAssets[indexToDuplicate]);
      updates.mediaAssets = [...mediaAssets, newMediaAssetId];
    }

    if (logos) {
      const newLogo = logos[indexToDuplicate];
      updates.logos = [...logos, newLogo];
    }

    updatePost(updates);
    setSelectedCreativeIndexToEdit(newCreatives.length - 1);
  }, [
    creatives,
    updatePost,
    selectedCreativeIndexToEdit,
    setSelectedCreativeIndexToEdit,
    mediaAssets,
    logos
  ]);

  const removeCreative = (e, index) => {
    e.stopPropagation();

    if (!isDeletingMediaAsset) {
      handleRemoveCreative(index);
    }
  };

  useEffect(() => {
    const requestCreativeAssetsUpdate =
      creativeAssets.length > 0 &&
      (!isEqual(previousCreativeAssets, creativeAssets) || refreshCreativeAssets);

    if (requestCreativeAssetsUpdate) {
      Actions.getAssetsPerformanceEditor(creativeAssets.map((asset) => asset.id));

      const ref = Actions.getAssetsPerformanceEditor.completed.listen(async (result) => {
        let assets = result.meta.payload;

        const orientation = (id) =>
          creativeAssets.filter((creativeAsset) => creativeAsset.id === id)[0]?.orientation || null;

        assets = assets.map((asset) => ({
          ...asset,
          orientation: orientation(asset.id) || "landscape"
        }));

        ref();
        await updateCreativeAssets(creatives, assets, updatePost);
      });

      setRefreshCreativeAssets(false);
    }
  }, [refreshCreativeAssets, creativeAssets]);

  const handleRemoveCreative = async (index, mediaAssetId = null) => {
    const newCreatives = [...creatives];
    newCreatives.splice(index, 1);

    const mediaAssetIdFiltered = mediaAssets[index];

    const newMediaAssets = await handleRemoveMediaAsset({
      mediaAssetId: mediaAssetId || mediaAssetIdFiltered,
      index
    });

    if (selectedCreativeIndexToEdit === index) {
      setSelectedCreativeIndexToEdit(0);
    } else if (selectedCreativeIndexToEdit > index) {
      setSelectedCreativeIndexToEdit(selectedCreativeIndexToEdit - 1);
    }

    const updates = {
      creatives: newCreatives,
      mediaAssets: newMediaAssets
    };

    if (logos) {
      const newLogos = [...logos];
      newLogos.splice(index, 1);
      updates.logos = newLogos;
    }

    updatePost(updates);
    setRefreshCreativeAssets(true);
  };

  const handleRemoveMediaAsset = async ({ mediaAssetId, index }) => {
    const newMediaAssets = [...mediaAssets];
    newMediaAssets.splice(index, 1);

    await PerformanceEditorActions.deleteMultipleMediaAssets({ mediaAssetIds: [mediaAssetId] });

    return newMediaAssets;
  };

  const selectMediaAssetCreative = ({ creative, index }) => {
    TemplatePostActions.updateTemplateId(creative.templateId, creative.type);
    setSelectedCreativeIndexToEdit(index);
  };

  if (!allowMultipleCreativesPerPost) {
    return null;
  }

  const performanceCreatives = () => {
    return renderedAssets
      .filter((creative) => !!creative)
      .map((creative, index) => {
        const canRenderMediaAssets =
          performanceMediaAssets.length &&
          mediaAssets.length &&
          MEDIA_ASSET_CHANNELS.includes(channel);

        let inputPropMediaAsset = "";
        if (canRenderMediaAssets) {
          const showVideoPreview = creative.type === "video" && creative.templateId === "RawVideo";

          inputPropMediaAsset = showVideoPreview
            ? creative.inputProps?.video
            : creative.inputProps?.backgroundImage;
        }

        const renderedCreative = canRenderMediaAssets ? inputPropMediaAsset : creative;

        return (
          <CreativeWrapper
            key={`${renderedCreative}_${index}`}
            onClick={() => selectMediaAssetCreative({ creative, index })}
            selected={selectedCreativeIndexToEdit === index}
          >
            <Creative url={renderedCreative} />
            {editable && creatives.length > 1 && !isDeletingMediaAsset && (
              <RemoveButton onClick={(e) => removeCreative(e, index)}>
                <ClearIcon />
              </RemoveButton>
            )}
          </CreativeWrapper>
        );
      });
  };

  return (
    <PerformanceEditorWrapper padding={padding} className="preview-performance">
      {!showEditor ? (
        <EditButton onClick={openPerformanceEditor}>
          <PerformanceIcon style={{ marginRight: "4px", marginTop: "-2px" }} />
          {t("Weitere Bilder hinzufügen")}
        </EditButton>
      ) : (
        <CreativesWrapper>
          {performanceCreatives()}
          {editable && creatives.length < 10 && (
            <Tooltip
              label={t(
                "Add another photo from the media library to attach it to the performance ad."
              )}
            >
              <CreativeWrapper onClick={addCreative}>
                <AddIcon />
              </CreativeWrapper>
            </Tooltip>
          )}
        </CreativesWrapper>
      )}
    </PerformanceEditorWrapper>
  );
};

export default withRefluxStore(
  PerformanceEditor,
  [PreviewStore, PerformanceEditorStore, TemplatePostStore],
  [
    "allowMultipleCreativesPerPost",
    "performanceMediaAssets",
    "isFetchingPerformanceMediaAsset",
    "inputProps",
    "isDeletingMediaAsset"
  ]
);
